import { useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { TriangleAlert } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { useClerk } from '@clerk/clerk-react'
import { useCurrentHousehold } from '@/hooks/useCurrentHousehold'
import { useHousehold } from '@/hooks/useHousehold'
import {
  archiveHousehold,
  unarchiveHousehold,
  leaveHousehold,
  deleteHousehold,
  resetHousehold,
  fetchMyHouseholds,
} from '@/api/households'
import { deleteAccount } from '@/api/users'
import { currentHousehold } from '@/lib/currentHousehold'
import { signOutAndReset } from '@/lib/signOut'
import { ROUTES } from '@/routes/routes'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { SettingsPanel, SettingsRow, ROW_BUTTON, DANGER_BUTTON } from './SettingsPanel'

type Dialog = 'archive' | 'leave' | 'reset' | 'deleteHousehold' | 'deleteAccount' | null

/** Everything that can't be undone, in one place: the current household's, then your account's. */
export function DangerZonePanel() {
  const { data: household, isLoading } = useHousehold()
  const { role, isSample, switchHousehold } = useCurrentHousehold()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const clerk = useClerk()

  const isOwner = role === 'OWNER'
  const isArchived = !!household?.archivedAt

  const [dialog, setDialog] = useState<Dialog>(null)
  const [busy, setBusy] = useState(false)
  const [isUnarchiving, setIsUnarchiving] = useState(false)
  const [confirmText, setConfirmText] = useState('')
  const [actionError, setActionError] = useState<string | null>(null)

  function open(next: Exclude<Dialog, null>) {
    setActionError(null)
    setConfirmText('')
    setDialog(next)
  }
  function close() {
    setDialog(null)
    setActionError(null)
  }

  /** Used whenever an action means the caller can no longer stay "on" the household they were
   *  just viewing (left, or permanently deleted) — moves to another active household if they
   *  have one (switching is how you'd get back to this one anyway, via the switcher, since it's
   *  no longer excluded from the list), otherwise falls back to a full reset. */
  async function afterLosingCurrentHousehold(lostHouseholdId: string) {
    const freshHouseholds = await queryClient.fetchQuery({
      queryKey: ['myHouseholds'],
      queryFn: fetchMyHouseholds,
    })
    const other = freshHouseholds.find((h) => h.id !== lostHouseholdId && !h.archivedAt)
    if (other) {
      switchHousehold(other.id)
    } else {
      currentHousehold.clear()
      queryClient.clear()
      navigate(ROUTES.APP.DASHBOARD, { replace: true })
    }
  }

  async function run(action: () => Promise<void>, failure: string) {
    setBusy(true)
    setActionError(null)
    try {
      await action()
    } catch {
      setActionError(failure)
    } finally {
      setBusy(false)
    }
  }

  const onArchive = () =>
    run(async () => {
      if (!household) return
      await archiveHousehold(household.id)
      close()
      // Archiving doesn't remove you from the household — you're still a member, just read-only
      // now — so stay put and refresh in place, rather than switching away like leave/delete do.
      await queryClient.invalidateQueries({ queryKey: ['household'] })
      await queryClient.invalidateQueries({ queryKey: ['myHouseholds'] })
      toast.success('Household archived — it’s now read-only.')
    }, 'Something went wrong. Please try again.')

  const onLeave = () =>
    run(async () => {
      if (!household) return
      await leaveHousehold(household.id)
      close()
      await afterLosingCurrentHousehold(household.id)
      toast.success('You left the household.')
    }, 'Something went wrong. Please try again.')

  const onDeleteHousehold = () =>
    run(async () => {
      if (!household) return
      await deleteHousehold(household.id, confirmText)
      close()
      await afterLosingCurrentHousehold(household.id)
      toast.success('Household permanently deleted.')
    }, 'Household name did not match, or something went wrong. Please try again.')

  const onReset = () =>
    run(async () => {
      await resetHousehold(confirmText)
      close()
      await queryClient.invalidateQueries()
      navigate(ROUTES.APP.DASHBOARD)
      toast.success('Household erased — you’re starting fresh.')
    }, 'Nothing was erased. Check the household name and try again.')

  async function onUnarchive() {
    if (!household) return
    setIsUnarchiving(true)
    setActionError(null)
    try {
      await unarchiveHousehold(household.id)
      await queryClient.invalidateQueries({ queryKey: ['household'] })
      await queryClient.invalidateQueries({ queryKey: ['myHouseholds'] })
      toast.success('Household restored — it’s no longer read-only.')
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setActionError(
        detail === 'HOUSEHOLD_LIMIT_REACHED'
          ? "You're already in 3 active households — leave or archive one first."
          : 'Something went wrong. Please try again.'
      )
    } finally {
      setIsUnarchiving(false)
    }
  }

  async function onDeleteAccount() {
    setBusy(true)
    setActionError(null)
    try {
      await deleteAccount()
      close()
      await signOutAndReset(clerk, ROUTES.AUTH.SIGN_IN)
    } catch (err: unknown) {
      const response = (err as { response?: { status?: number; data?: { detail?: string } } })
        ?.response
      setActionError(
        response?.status === 409
          ? (response.data?.detail ??
              'Transfer ownership to another member first, then delete your account.')
          : 'Something went wrong. Please try again.'
      )
      setBusy(false)
    }
  }

  const leaveRow = (
    <SettingsRow
      label="Leave household"
      hint="You'll lose access to this household's accounts, transactions, budgets, and goals. Other households you're in aren't affected."
    >
      <DangerButton onClick={() => open('leave')}>Leave household</DangerButton>
    </SettingsRow>
  )
  const householdName = household?.name ?? ''

  return (
    <SettingsPanel
      id="danger"
      title="Danger zone"
      aside="These can't be undone"
      danger
      overlays={
        <>
          {dialog === 'archive' && (
            <ConfirmDialog
              title={`Archive "${householdName}"?`}
              description="It becomes fully read-only for every member, owner included, until you restore it. Nothing is deleted."
              error={actionError}
              confirmLabel={busy ? 'Archiving…' : 'Archive'}
              confirmDisabled={busy}
              onConfirm={onArchive}
              onCancel={close}
            />
          )}
          {dialog === 'leave' && (
            <ConfirmDialog
              title={`Leave "${householdName}"?`}
              description="You'll lose access to everything in this household. This doesn't affect any other household you're in."
              error={actionError}
              confirmLabel={busy ? 'Leaving…' : 'Leave'}
              confirmDisabled={busy}
              onConfirm={onLeave}
              onCancel={close}
            />
          )}
          {dialog === 'reset' && household && (
            <ResetDialog
              householdName={householdName}
              confirmText={confirmText}
              onConfirmTextChange={setConfirmText}
              error={actionError}
              isResetting={busy}
              onConfirm={onReset}
              onCancel={close}
            />
          )}
          {dialog === 'deleteHousehold' && household && (
            <TypeToConfirmDialog
              title={`Delete "${householdName}"?`}
              description={
                <>
                  This permanently deletes every account, transaction, budget, and goal in this
                  household. This cannot be undone. Type the household name{' '}
                  <strong>{householdName}</strong> to confirm.
                </>
              }
              expected={householdName}
              value={confirmText}
              onChange={setConfirmText}
              error={actionError}
              confirmLabel={busy ? 'Deleting…' : 'Delete permanently'}
              busy={busy}
              onConfirm={onDeleteHousehold}
              onCancel={close}
            />
          )}
          {dialog === 'deleteAccount' && (
            <TypeToConfirmDialog
              title="Delete your account?"
              description={
                <>
                  This permanently deletes your sign-in identity. Your past transactions stay in the
                  household's history, shown as "Deleted User". Type <strong>DELETE</strong> to
                  confirm.
                </>
              }
              expected="DELETE"
              placeholder="Type DELETE"
              value={confirmText}
              onChange={setConfirmText}
              error={actionError}
              confirmLabel={busy ? 'Deleting…' : 'Delete Account'}
              busy={busy}
              onConfirm={onDeleteAccount}
              onCancel={close}
            />
          )}
        </>
      }
    >
      {actionError && !dialog && (
        <div className="my-2 rounded-lg bg-[var(--fl-surface)] px-4 py-3 text-sm text-[var(--fl-error)]">
          {actionError}
        </div>
      )}

      {/* The current household's rows depend on whether it's archived and whether the caller owns
          it. Switching to a different household via the switcher shows that household's own rows. */}
      {!isLoading && !isSample && !isArchived && isOwner && (
        <>
          <SettingsRow
            label="Start fresh"
            hint="Erase every account, transaction, budget, goal, person and category. Members, invites and your plan stay."
          >
            <DangerButton onClick={() => open('reset')}>Start fresh</DangerButton>
          </SettingsRow>
          <SettingsRow
            label="Archive household"
            hint="Makes it read-only for every member, you included. Nothing is deleted — you can restore it any time."
          >
            <DangerButton onClick={() => open('archive')}>Archive household</DangerButton>
          </SettingsRow>
        </>
      )}

      {!isLoading && !isSample && !isArchived && !isOwner && leaveRow}

      {!isLoading && isArchived && isOwner && (
        <SettingsRow
          label="Archived household"
          hint="This household is read-only. Restore it to normal use, or permanently delete it and everything in it."
        >
          <Button
            variant="outline"
            className={cn(ROW_BUTTON, 'bg-[var(--fl-surface)]')}
            disabled={isUnarchiving}
            onClick={onUnarchive}
          >
            {isUnarchiving ? 'Restoring…' : 'Unarchive'}
          </Button>
          <DangerButton onClick={() => open('deleteHousehold')}>Delete permanently</DangerButton>
        </SettingsRow>
      )}

      {!isLoading && isArchived && !isOwner && leaveRow}

      <SettingsRow
        label="Delete my account"
        hint="Permanently deletes your sign-in and profile. Your household's history stays for other members."
      >
        <DangerButton onClick={() => open('deleteAccount')}>Delete my account</DangerButton>
      </SettingsRow>
    </SettingsPanel>
  )
}

function DangerButton({ onClick, children }: { onClick: () => void; children: string }) {
  return (
    <Button
      type="button"
      variant="outline"
      onClick={onClick}
      className={cn(ROW_BUTTON, DANGER_BUTTON)}
    >
      {children}
    </Button>
  )
}

function DialogShell({ children }: { children: ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="w-full max-w-sm rounded-xl bg-[var(--fl-surface)] p-6 shadow-[rgba(0,0,0,0.15)_0px_8px_40px]">
        {children}
      </div>
    </div>
  )
}

function DialogError({ error }: { error: string | null }) {
  return error ? (
    <div className="mb-4 rounded-lg bg-[var(--fl-error-bg)] px-4 py-3 text-sm text-[var(--fl-error)]">
      {error}
    </div>
  ) : null
}

function ConfirmDialog({
  title,
  description,
  error,
  confirmLabel,
  confirmDisabled,
  onConfirm,
  onCancel,
}: {
  title: string
  description: string
  error: string | null
  confirmLabel: string
  confirmDisabled: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  return (
    <DialogShell>
      <h3 className="mb-2 text-base font-semibold text-[var(--fl-text-1)]">{title}</h3>
      <p className="mb-4 text-sm text-[var(--fl-text-2)]">{description}</p>
      <DialogError error={error} />
      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" variant="destructive" onClick={onConfirm} disabled={confirmDisabled}>
          {confirmLabel}
        </Button>
      </div>
    </DialogShell>
  )
}

function TypeToConfirmDialog({
  title,
  description,
  expected,
  placeholder,
  value,
  onChange,
  error,
  confirmLabel,
  busy,
  onConfirm,
  onCancel,
}: {
  title: string
  description: ReactNode
  expected: string
  placeholder?: string
  value: string
  onChange: (value: string) => void
  error: string | null
  confirmLabel: string
  busy: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  return (
    <DialogShell>
      <h3 className="mb-2 text-base font-semibold text-[var(--fl-text-1)]">{title}</h3>
      <p className="mb-4 text-sm text-[var(--fl-text-2)]">{description}</p>
      <DialogError error={error} />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder ?? expected}
        autoComplete="off"
        className="mb-4"
      />
      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="destructive"
          onClick={onConfirm}
          disabled={value !== expected || busy}
        >
          {confirmLabel}
        </Button>
      </div>
    </DialogShell>
  )
}

function ResetDialog({
  householdName,
  confirmText,
  onConfirmTextChange,
  error,
  isResetting,
  onConfirm,
  onCancel,
}: {
  householdName: string
  confirmText: string
  onConfirmTextChange: (value: string) => void
  error: string | null
  isResetting: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="reset-dialog-title"
        aria-describedby="reset-dialog-warning"
        className="w-full max-w-md rounded-xl border border-[var(--fl-error)] bg-[var(--fl-surface)] p-6 shadow-[rgba(0,0,0,0.15)_0px_8px_40px]"
      >
        <div className="mb-4 flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--fl-error-bg)]">
            <TriangleAlert className="h-5 w-5 text-[var(--fl-error)]" aria-hidden="true" />
          </div>
          <h3
            id="reset-dialog-title"
            className="pt-2 text-base font-semibold text-[var(--fl-text-1)]"
          >
            Erase everything in "{householdName}"?
          </h3>
        </div>

        <div
          id="reset-dialog-warning"
          className="mb-4 rounded-lg bg-[var(--fl-error-bg)] px-4 py-3 text-sm font-medium text-[var(--fl-error)]"
        >
          This cannot be undone. Erased data is gone for good — there is no recovery.
        </div>

        <div className="mb-4 space-y-2 text-sm text-[var(--fl-text-2)]">
          <p>
            <span className="font-medium text-[var(--fl-text-1)]">Erased:</span> all accounts, loans
            and cards, transactions and transfers, budgets, goals, recurring items, people and
            shared expenses, and your categories (the default set comes back).
          </p>
          <p>
            <span className="font-medium text-[var(--fl-text-1)]">Kept:</span> the household, its
            members and invites, and your plan.
          </p>
          <p>
            Want a copy first?{' '}
            <Link
              to={ROUTES.APP.SETTINGS_EXPORT}
              onClick={onCancel}
              className="text-[var(--fl-primary)] underline"
            >
              Export your data
            </Link>
            .
          </p>
        </div>

        <label className="mb-1 block text-sm text-[var(--fl-text-1)]">
          Type <strong>{householdName}</strong> to confirm
        </label>
        <Input
          value={confirmText}
          onChange={(e) => onConfirmTextChange(e.target.value)}
          placeholder={householdName}
          autoComplete="off"
          className="mb-4"
        />

        {error && (
          <div className="mb-4 rounded-lg bg-[var(--fl-error-bg)] px-4 py-3 text-sm text-[var(--fl-error)]">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isResetting}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={onConfirm}
            disabled={confirmText !== householdName || isResetting}
          >
            {isResetting ? 'Erasing…' : 'Erase everything'}
          </Button>
        </div>
      </div>
    </div>
  )
}
